import React, { useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import { NavigationTrap, mergeRefs } from '@ds/react-utils'
import { MotionPresence, MotionVariant } from '@ds/motion'
import {
  StopPropagation,
  EventListenerTypes,
} from '../../internal/components/StopPropagation'
import { CustomPropTypes } from '../../support'
import type { ConditionalReactElementChildOrArray } from '../../types'
import { useDsUiDataProps } from '../../theming'
import { EscapeToClose } from '../../utilities'
import { globalIds } from '../../variables'
import { Scrim } from '../Scrim'
import { ModalBase, ModalBaseProps } from './ModalBase'
import { ModalClose } from './ModalClose'
import { ModalHeader, ModalHeaderProps } from './ModalHeader'
import { ModalBody, ModalBodyProps } from './ModalBody'
import { ModalFooter, ModalFooterProps } from './ModalFooter'
import { ModalPortal } from './ModalPortal'
import {
  useDisableBodyScroll,
  useFocusReturn,
  useOnOpenCallback,
  useUnmountWarning,
} from './Modal.hooks'
import { useInitialFocus } from '../../hooks/useInitialFocus'
import { useAnimationStatus } from '../../hooks/useAnimationStatus'

export const modalHeights = ['content', 'fixed', 'window'] as const
export type ModalHeight = (typeof modalHeights)[number]

export const modalWidths = ['xlarge', 'large', 'medium', 'small'] as const
export type ModalWidth = (typeof modalWidths)[number]

export const modalRoles = ['alertdialog', 'dialog'] as const
export type ModalRoles = (typeof modalRoles)[number]

export type ModalImperativeHandle = {
  inertSiblings: boolean
  returnFocus?: () => void
}

export interface ModalProps
  extends Omit<ModalBaseProps, 'disabled' | 'children'> {
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * The children of the Modal
   */
  children: ConditionalReactElementChildOrArray<
    ModalHeaderProps | ModalBodyProps | ModalFooterProps
  >
  /**
   * Whether the Modal should stop the propagation of all events.
   */
  disableEventPropagation?: boolean
  /**
   * An array of React events that will be allowed to propagate up the DOM tree past the Modal. If an event, or events are passed, the Modal will stop propagation of all other events.
   * @example {['onClick']}
   */
  eventsToPropagate?: Array<keyof EventListenerTypes>
  /**
   * Set the Modal's portal siblings to inert
   */
  inertSiblings?: boolean
  /**
   * A selector or ref specifying the DOM element to set initial focus on once the Modal animates in.
   *
   * Default initial focus is at the top of the Modal.
   * This prop can be used to customize the initial focus,
   * particularly for modals which contain an error message, alert, or warning.
   * In these cases, initial focus should be the least destructive button, such as "Stay Logged In" or "Cancel".
   *
   * See the [Alert Modal configuration](./index.html?path=/docs/components-modal-configurations-developer--alert-modal) for an example.
   */
  initialFocus?: string | React.RefObject<HTMLElement>
  /**
   * @ignore @internal Used to determine if the Modal is a child of a ModalPortal
   */
  _modalPortalChild?: boolean
  /**
   * A function to invoke when the Modal requests to be closed.
   *
   * This request is made when:
   *   - the "Escape" key is pressed
   *   - the document receives a click outside of the Modal
   */
  onClose?: (event: Event | React.SyntheticEvent) => void
  /**
   * A function to invoke when the Modal is fully loaded in.
   */
  onOpen?: () => void
  /**  A function to invoke when the Modal animation out completes */
  onHideComplete?: () => void
  /**  A function to invoke when the Modal animation in completes */
  onShowComplete?: () => void
  /**
   * @ignore @internal
   */
  _modalPortalRef?: React.RefObject<ModalImperativeHandle>
  /**
   * Whether the Modal is shrouded by another Modal displayed above it.
   *
   * The effect is to disable the Modal and dismiss the associated Scrim.
   */
  shrouded?: boolean
  /**
   * Displays the modal.
   */
  visible?: boolean
}

/**
 * Modals function as a method to temporarily focus the user’s attention away
 * from all other tasks in order to communicate a brief message or to complete
 * a short series of actions.
 */
export function Modal({
  accessibilityTitle,
  children,
  closeButton,
  'data-qa': dataQa,
  disableEventPropagation = false,
  eventsToPropagate,
  forwardedRef,
  height = 'content',
  inertSiblings = false,
  initialFocus,
  _modalPortalChild,
  _modalPortalRef,
  onClose,
  onOpen,
  onHideComplete,
  onShowComplete,
  role = 'dialog',
  shrouded = false,
  visible = false,
  width = 'medium',
  ...restProps
}: ModalProps) {
  useUnmountWarning(visible)

  const dsUiDataProps = useDsUiDataProps(Modal)

  const [modalElement, setModalElement] = useState<HTMLElement | null>(null)

  const { onAnimationStart, onAnimationEnd, showComplete } = useAnimationStatus(
    onHideComplete,
    onShowComplete,
  )

  /**
   * useFocusReturn needs to run before any other hooks that may move focus
   * to ensure that it is able to capture the element that had focus before
   * the modal was opened.
   */
  const { focusPrevious } = useFocusReturn(visible)

  /**
   * If Modal has a parent ModalPortal, pass a function that returns focus to the
   * element focused when the Modal was opened. This allows ModalPortal to return
   * focus after executing necessary clean up tasks (`aria-hidden` & `inert` removal).
   *
   * Without this, useFocusReturn will call focus on an inert element (no effect).
   *
   * https://github.docusignhq.com/front-end-systems/ds-ui/pull/4160/files
   * ^^^ Race condition fix for when focus would not go back because siblings were inert.
   */
  useImperativeHandle(_modalPortalRef, () => ({
    inertSiblings,
    returnFocus() {
      focusPrevious()
    },
  }))

  const { defaultInitialFocusRef, trapConfig } = useInitialFocus(
    initialFocus,
    showComplete,
    visible,
  )

  useOnOpenCallback(modalElement, visible, onOpen)

  useDisableBodyScroll(modalElement, visible)

  const defaultInitialFocus = (
    <span
      data-qa="modal-default-initial-focus"
      css={{ outline: 'none' }}
      ref={defaultInitialFocusRef}
      tabIndex={-1}
    />
  )

  const ModalWithAnimation = (
    <MotionVariant
      initial="initial"
      animate="enterOpacity"
      exit="exitOpacity"
      onAnimationStart={onAnimationStart}
      inert={shrouded ? 'true' : undefined}
      onAnimationEnd={onAnimationEnd}
    >
      <NavigationTrap {...trapConfig}>
        <ModalBase
          {...dsUiDataProps}
          {...restProps}
          data-qa={dataQa}
          accessibilityTitle={accessibilityTitle}
          forwardedRef={mergeRefs(forwardedRef || undefined, setModalElement)}
          closeButton={closeButton}
          zIndex={0}
          disabled={shrouded}
          height={height}
          width={width}
          role={role}
        >
          {defaultInitialFocus}
          {children}
        </ModalBase>
      </NavigationTrap>
    </MotionVariant>
  )

  const shouldStopPropagation =
    disableEventPropagation || !!eventsToPropagate?.length

  const renderModalBase = () =>
    shouldStopPropagation ? (
      <StopPropagation ignoreEvents={eventsToPropagate}>
        {ModalWithAnimation}
      </StopPropagation>
    ) : (
      ModalWithAnimation
    )

  const ModalWithScrim = (
    <EscapeToClose onClose={onClose} enabled={visible && !shrouded}>
      <Scrim
        data-qa={dataQa && `${dataQa}-scrim`}
        onClick={onClose}
        visible={visible && !shrouded}
        zIndex={0}
      />
      <MotionPresence>{visible && renderModalBase()}</MotionPresence>
    </EscapeToClose>
  )

  return _modalPortalChild ? (
    ModalWithScrim
  ) : (
    <ModalPortal
      targetId={globalIds.ModalContainer}
      /**
       * ModalPortal will call `onEmpty` when its innerHTML is empty and it has done
       * all necessary clean up tasks (removing `aria-hidden` and `inert` attributes).
       */
      onEmpty={focusPrevious}
      inertSiblings={inertSiblings}
    >
      {ModalWithScrim}
    </ModalPortal>
  )
}

Modal.heights = modalHeights
Modal.widths = modalWidths
Modal.roles = modalRoles

Modal.propTypes = {
  accessibilityTitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  closeButton: PropTypes.node,
  'data-.*': PropTypes.string,
  disableEventPropagation: PropTypes.bool,
  eventsToPropagate: PropTypes.arrayOf(PropTypes.string),
  height: PropTypes.oneOf(Modal.heights),
  inertSiblings: PropTypes.bool,
  initialFocus: PropTypes.oneOfType([
    PropTypes.string,
    CustomPropTypes.ReactRefObject,
  ]),
  onClose: PropTypes.func,
  onHideComplete: PropTypes.func,
  onOpen: PropTypes.func,
  onShowComplete: PropTypes.func,
  role: PropTypes.oneOf(Modal.roles),
  shrouded: PropTypes.bool,
  visible: PropTypes.bool,
  width: PropTypes.oneOf(Modal.widths),
}

Modal.displayName = 'Modal'

Modal.Base = ModalBase
Modal.Close = ModalClose
Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter
Modal.Portal = ModalPortal
